import {
    AfterViewInit,
    Component,
    computed,
    ContentChild,
    DestroyRef,
    ElementRef,
    input,
    output,
    signal,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {NgClass, NgStyle} from '@angular/common';
import {DocumentService} from '../../services/document.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ButtonService} from '../../services/button.service';
import {Menu} from '../menu/menu.component';

/**
 * Botão multifuncional da biblioteca.
 *
 * Suporta rótulo e/ou ícone, formatos ({@link shape}), tamanhos ({@link size}),
 * estados (desabilitado, toggle) e um menu suspenso opcional (projetado como
 * `ai-menu`), cujo posicionamento é calculado dinamicamente em relação à
 * viewport.
 *
 * Usa `ViewEncapsulation.None` de propósito: as classes `.ai-button*` são
 * reutilizadas por outros componentes (ex.: `ai-select-button`, `ai-input-actions`),
 * portanto precisam permanecer globais.
 */
@Component({
    selector: 'ai-button',
    standalone: true,
    imports: [NgClass, NgStyle],
    templateUrl: './button.component.html',
    styleUrls: ['./button.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class Button implements AfterViewInit {
    protected hasMenu: boolean = false;
    protected readonly isMenuVisible = signal<boolean>(false);
    protected readonly cssClass = signal<string>('ai-button');

    @ContentChild(Menu) menuElement?: Menu;
    // Referência para o menu e o botão.
    @ViewChild('button') button!: ElementRef;
    @ViewChild('buttonmenu') menu!: ElementRef;

    protected computedClasses = computed((): Record<string, unknown> => {
        return {
            'ai-button': true,
            'ai-button-with-label': this.label() && this.label().length > 0,
            'ai-button-shape-round': this.shape() === 'round',
            'ai-button-shape-square': this.shape() === 'square',
            'ai-button-shape-base-rounder': this.shape() === 'base-rounder',
            'ai-button-border-outlined': this.border() === 'outlined',
            'ai-button-border-hidden': this.border() === 'hidden',
            'ai-button-transparent': this.transparent(),
            'ai-button-small': this.size() === 'small',
            'ai-button-large': this.size() === 'large',
            'ai-button-disabled': this.disabled(),
            'ai-button-toggled': this.isSelected(),
            'ai-button-tag': this.tag(),
            'ai-button-primary': this.variant() === 'primary',
            'ai-button-danger': this.variant() === 'danger',
            ...this.styleClass(),
        };
    });

    // Inputs

    /**
     * Texto do botão.
     */
    label = input<string>('');

    /**
     * Se o botão está desabilitado.
     */
    disabled = input<boolean>(false);

    /**
     * Ícone do botão.
     */
    icon = input<string>('');

    type = input<string>('button');

    /**
     * Define o formato/shape do botão.
     */
    shape = input<'base-rounder' | 'round' | 'square' | 'circle'>('round');

    /**
     * Define o formato da borda do botão.
     */
    border = input<'outlined' | 'hidden'>('outlined');

    /**
     * Define se o botão deve ter o background transparente.
     */
    transparent = input<boolean>(false);

    /**
     * Variante de cor do botão.
     *
     * - `default`: aparência padrão (fundo neutro, texto na cor primária).
     * - `primary`: preenchido com a cor de destaque, para a ação principal
     *   de uma tela (ex.: o botão de confirmar de um formulário).
     * - `danger`: aparência de alerta (fundo vermelho bem claro, borda e
     *   texto/ícone vermelhos), para ações destrutivas/de descarte.
     */
    variant = input<'default' | 'primary' | 'danger'>('default');

    /**
     * Define se o botão deve ser uma tag
     */
    tag = input<boolean>(false);

    /**
     * Define uma classe de estilo extra para ser aplicada ao botão.
     */
    styleClass = input<{}>({});

    /**
     * Define um estilo CSS extra para ser aplicado ao botão.
     */
    style = input<any>({});

    /**
     * Define a localização do ícone dentro do botão.
     */
    iconPosition = input<'top' | 'bottom' | 'left' | 'right'>('left');

    /**
     * Define o tamanho do botão.
     */
    size = input<'small' | 'medium' | 'large'>('medium');

    /**
     * Define o local onde o menu deve aparecer.
     */
    menuPosition = input<'top' | 'bottom'>('bottom');

    /**
     * Define o comportamento do botão ao ser clicado.
     *
     * Se for toggle, vai aplicar ao botão um estilo que muda seu
     * visual enquanto estiver no estado "selected".
     *
     * Se for default, terá o comportamento padrão de um botão.
     *
     */
    mode = input<'default' | 'toggle'>('default');

    /**
     * Texto do tooltip que deve ser exibido ao passar o mouse sobre o botão.
     */
    tooltip = input<string>('');

    /**
     * Define se o menu deve ser ocultado quando o mouse sai do botão.
     */
    hideMenuOnMouseOut = input<boolean>(false);

    // Outputs

    /**
     * Evento de clique no botão.
     */
    click = output<any>();

    /**
     * Evento disparado quando o mouse deixa o botão.
     */
    mouseOut = output<void>();

    private mouseOutHideMenuTimeout: ReturnType<typeof setTimeout> | null =
        null;

    protected isSelected = signal<boolean>(false);
    protected top = signal<string>('0px');
    protected left = signal<string>('0px');
    protected maxHeight = signal<string>('');

    constructor(
        private eRef: ElementRef,
        private documentService: DocumentService,
        private buttonService: ButtonService,
        private destroyRef: DestroyRef,
    ) {
        this.hasMenu = !!this.menuElement;
    }

    ngAfterViewInit(): void {
        // Evitar criar os subscribers caso o botão não tenha menu.
        if (this.menu && this.menu.nativeElement.childNodes.length > 0) {
            // Detectar cliques fora do botão para que o menu seja fechado.
            this.documentService
                .onDocumentClick()
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((event: Event | null) => {
                    if (
                        event &&
                        !this.eRef.nativeElement.contains(event.target)
                    ) {
                        this.closeMenu();
                    }
                });

            // Detectar cliques em outros botões para que o menu seja fechado.
            this.buttonService.buttonClicked$
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((event: Event | null) => {
                    if (
                        event &&
                        !this.eRef.nativeElement.contains(event.target)
                    ) {
                        this.closeMenu();
                    }
                });
        }
    }

    onButtonClick(event: MouseEvent): void {
        event.stopPropagation();

        // Informa que este botão foi clicado.
        this.buttonService.emitButtonClicked(event);

        // Inverte a visibilidade do menu
        this.isMenuVisible.update((prev) => !prev);

        if (this.isMenuVisible()) {
            // Primeira medida síncrona; segunda no próximo frame para capturar
            // conteúdo renderizado dentro do menu (itens async/ngFor).
            this.setMenuPosition();
            requestAnimationFrame(() => this.setMenuPosition());
        }

        if (this.mode() === 'toggle') {
            this.isSelected.update((prev) => !prev);
        }

        this.click.emit(event);
    }

    private setMenuPosition(): void {
        if (!this.menu) return;

        // `position: fixed` → usar viewport-relative (getBoundingClientRect),
        // não ElementPositions (que soma scrollY e quebra quando há scroll).
        const btnRect = this.button.nativeElement.getBoundingClientRect();
        const menuEl = this.menu.nativeElement as HTMLElement;
        const menuHeight = menuEl.scrollHeight;
        const menuWidth = menuEl.offsetWidth || menuEl.scrollWidth;
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const GAP = 6;
        const EDGE_PADDING = 8;
        // O wrapper `.ai-button-menu` tem padding invisível (bridge de hover). O
        // conteúdo do menu fica deslocado por esse padding, então compensamos
        // para que o menu apareça logo abaixo/acima do botão (não distante).
        const menuStyle = getComputedStyle(menuEl);
        const padTop = parseFloat(menuStyle.paddingTop) || 0;
        const padBottom = parseFloat(menuStyle.paddingBottom) || 0;

        const spaceBelow = viewportHeight - btnRect.bottom - GAP;
        const spaceAbove = btnRect.top - GAP;

        // Prefere a posição pedida; só troca de lado se não couber e o outro
        // lado tiver mais espaço. Assim o default `bottom` não é invertido
        // quando simplesmente há conteúdo grande — apenas limitamos a altura.
        const preference = this.menuPosition();
        const preferBelow =
            preference === 'bottom'
                ? menuHeight <= spaceBelow || spaceBelow >= spaceAbove
                : !(menuHeight <= spaceAbove || spaceAbove >= spaceBelow);

        if (preferBelow) {
            this.top.set(btnRect.bottom + GAP - padTop + 'px');
            this.maxHeight.set(
                Math.max(120, spaceBelow - EDGE_PADDING) + 'px',
            );
        } else {
            const available = Math.max(120, spaceAbove - EDGE_PADDING);
            const used = Math.min(menuHeight, available);
            this.top.set(btnRect.top - used - GAP + padBottom + 'px');
            this.maxHeight.set(available + 'px');
        }

        // Clamp horizontal para não escapar da tela pela direita nem esquerda.
        const desiredLeft = btnRect.left - 20;
        const maxLeft = Math.max(
            EDGE_PADDING,
            viewportWidth - menuWidth - EDGE_PADDING,
        );
        const clampedLeft = Math.min(
            Math.max(desiredLeft, EDGE_PADDING),
            maxLeft,
        );
        this.left.set(clampedLeft + 'px');
    }

    closeMenu(): void {
        this.isMenuVisible.set(false);
    }

    protected onMouseOverButton(_: Event): void {
        if (this.mouseOutHideMenuTimeout) {
            clearTimeout(this.mouseOutHideMenuTimeout);
            this.mouseOutHideMenuTimeout = null;
        }
    }

    protected onMouseOutButton(_: Event): void {
        if (this.hideMenuOnMouseOut()) {
            this.mouseOutHideMenuTimeout = setTimeout(() => {
                this.closeMenu();
            }, 250);
        }
        this.mouseOut.emit();
    }

    setMenu(menu: Menu): void {
    }
}
